import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Phone, MessageCircle, CheckCheck, Star, CalendarClock, Scissors, PartyPopper, RefreshCw } from 'lucide-react'
import styles from './CSMCelebrationInbox.module.css'
import AuthContext from '../../store/auth-context'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import { smPost, getTodayIso } from './smApi'
import { prettifyOccasionType, emojiForIcon } from './smIcons'
import SMSetupState from './SMSetupState'
import SMCoverageNudge from './SMCoverageNudge'

const SM_GET_CELEBRATION_INBOX = 'sm_get_celebration_inbox'
const SM_MARK_WISHED = 'sm_mark_wished'

// Filter chips are client-side only for now: the API's occasion_filter
// takes a numeric store_occasion_id, which we don't have a catalog
// endpoint for yet (that'll come with sm_get_settings). Once that's
// wired, swap these static chips for one per store_occasion_id and
// pass it straight through instead of filtering occasion_type locally.
const FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'birthday', label: '🎂 Birthdays' },
    { key: 'anniversary', label: '💍 Anniversaries' },
]

const buildWhatsAppLink = (phone, name) => {
    const digits = (phone || '').replace(/[^0-9]/g, '')
    const message = encodeURIComponent(`Hi ${name.split(' ')[0]}, wishing you a wonderful day! 🎉`)
    return `https://wa.me/${digits}?text=${message}`
}

const formatTime = (isoString) => {
    if (!isoString) return ''
    return new Date(isoString).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true })
}

const CSMCelebrationInbox = () => {
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    const [pending, setPending] = useState([])
    const [completed, setCompleted] = useState([])
    const [loading, setLoading] = useState(true)
    const [loadErrorMessage, setLoadErrorMessage] = useState(null)
    const [filter, setFilter] = useState('all')
    const [markingOccasionId, setMarkingOccasionId] = useState(null)
    const [hasOccasionData, setHasOccasionData] = useState(true)
    const [setupMessage, setSetupMessage] = useState(null)
    const [clientsWithOccasion, setClientsWithOccasion] = useState(0)
    const [totalClients, setTotalClients] = useState(0)

    const loadInbox = useCallback(async () => {
        setLoading(true)
        setLoadErrorMessage(null)
        const res = await smPost(authCtx, activeStore, SM_GET_CELEBRATION_INBOX, {
            date: getTodayIso(),
            occasion_filter: 'all',
        })
        if (res?.status === 'success' && res?.response) {
            setPending(res.response.pending || [])
            setCompleted(res.response.completed || [])
            setHasOccasionData(res.response.has_occasion_data !== false)
            setSetupMessage(res.response.setup_message || null)
            setClientsWithOccasion(res.response.clients_with_occasion || 0)
            setTotalClients(res.response.total_clients || 0)
        } else {
            setLoadErrorMessage(res?.message || "Couldn't load the Celebration Inbox right now.")
        }
        setLoading(false)
    }, [authCtx, activeStore])

    useEffect(() => {
        if (activeStore?.storeIdentifier) loadInbox()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier])

    const filteredPending = useMemo(
        () => (filter === 'all' ? pending : pending.filter((c) => c.occasion_type === filter)),
        [pending, filter]
    )
    const filteredCompleted = useMemo(
        () => (filter === 'all' ? completed : completed.filter((c) => c.occasion_type === filter)),
        [completed, filter]
    )

    const markWished = async (occasionId) => {
        const item = pending.find((c) => c.occasion_id === occasionId)
        if (!item || markingOccasionId) return

        setMarkingOccasionId(occasionId)
        const res = await smPost(authCtx, activeStore, SM_MARK_WISHED, {
            occasion_id: occasionId,
            channel: 'in_person',
            sent_by: authCtx?.authIdentifier,
            remarks: '',
        })
        setMarkingOccasionId(null)

        if (res?.status === 'success' && res?.response) {
            const { completed_via, completed_at } = res.response
            setPending((prev) => prev.filter((c) => c.occasion_id !== occasionId))
            setCompleted((prev) => [...prev, { ...item, completed_via, completed_at }])
        } else {
            toast.error(res?.message || "Couldn't mark this as wished right now.")
        }
    }

    const totalCount = pending.length + completed.length

    if (loading) {
        return (
            <div className={styles.loadingWrap}>
                <RefreshCw size={18} className={styles.spinner} />
                <span>Loading celebration inbox…</span>
            </div>
        )
    }

    if (loadErrorMessage) {
        return (
            <div className={styles.loadingWrap}>
                <span>{loadErrorMessage}</span>
                <button type="button" className={styles.retryBtn} onClick={loadInbox}>Retry</button>
            </div>
        )
    }

    if (!hasOccasionData) {
        return (
            <div className={styles.page}>
                <div className={styles.pageHeader}>
                    <h1 className={styles.pageTitle}>Today's Celebrations</h1>
                    <p className={styles.pageSubtitle}>Every customer as an action card — call, message, or wish them in a click.</p>
                </div>
                <SMSetupState message={setupMessage} />
            </div>
        )
    }

    const showCoverageNudge = totalClients > 0 && clientsWithOccasion < totalClients

    return (
        <div className={styles.page}>
            <div className={styles.pageHeader}>
                <h1 className={styles.pageTitle}>Today's Celebrations <span className={styles.countBadge}>({totalCount})</span></h1>
                <p className={styles.pageSubtitle}>Every customer as an action card — call, message, or wish them in a click.</p>
            </div>

            {showCoverageNudge && (
                <SMCoverageNudge clientsWithOccasion={clientsWithOccasion} totalClients={totalClients} />
            )}

            <div className={styles.filterRow}>
                {FILTERS.map((f) => (
                    <button
                        key={f.key}
                        type="button"
                        className={`${styles.filterChip} ${filter === f.key ? styles.filterChipActive : ''}`}
                        onClick={() => setFilter(f.key)}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            {/* Pending */}
            <div className={styles.sectionHeaderRow}>
                <span className={styles.sectionLabel}>Pending</span>
                <span className={styles.sectionCount}>{filteredPending.length}</span>
            </div>

            {filteredPending.length === 0 && (
                <div className={styles.emptyState}>Nothing pending in this filter — everyone's been wished! 🎉</div>
            )}

            <div className={styles.cardList}>
                {filteredPending.map((c) => {
                    const label = prettifyOccasionType(c.occasion_type)
                    const detail = c.turning_age != null
                        ? `Turns ${c.turning_age}`
                        : c.years_together != null
                            ? `${c.years_together} Years Together`
                            : null
                    return (
                        <div key={c.occasion_id} className={styles.actionCard}>
                            <div className={styles.actionCardTop}>
                                <span className={styles.cardEmoji}>{emojiForIcon(c.icon)}</span>
                                <div className={styles.cardHeaderText}>
                                    <Link to={`/special-moments/customer/${c.client_store_relation_identifier}`} className={styles.cardName}>
                                        {c.name}
                                    </Link>
                                    <div className={styles.cardOccasion}>{label}</div>
                                </div>
                                {c.is_vip && (
                                    <span className={styles.vipBadge}>
                                        <Star size={11} fill="currentColor" /> VIP
                                    </span>
                                )}
                            </div>

                            {detail && <div className={styles.cardDetailRow}>{detail}</div>}

                            <div className={styles.metaGrid}>
                                <div className={styles.metaItem}>
                                    <span className={styles.metaLabel}>Customer Since</span>
                                    <span className={styles.metaValue}>{c.customer_since}</span>
                                </div>
                                <div className={styles.metaItem}>
                                    <CalendarClock size={13} className={styles.metaIcon} />
                                    <span className={styles.metaValue}>Visited {c.last_visit_days_ago} Days Ago</span>
                                </div>
                                <div className={styles.metaItem}>
                                    <Scissors size={13} className={styles.metaIcon} />
                                    <span className={styles.metaValue}>{c.favorite_stylist}</span>
                                </div>
                            </div>

                            <div className={styles.actionsRow}>
                                <a href={`tel:${c.phone}`} className={styles.actionBtn}>
                                    <Phone size={14} /> Call
                                </a>
                                <a
                                    href={buildWhatsAppLink(c.phone, c.name)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.actionBtn}
                                >
                                    <MessageCircle size={14} /> WhatsApp
                                </a>
                                <Link
                                    to={`/special-moments/wish/${c.client_store_relation_identifier}/${c.occasion_id}`}
                                    className={`${styles.actionBtn} ${styles.actionBtnPrimary}`}
                                >
                                    <PartyPopper size={14} /> Wish
                                </Link>
                                <button
                                    type="button"
                                    className={styles.actionBtn}
                                    disabled={markingOccasionId === c.occasion_id}
                                    onClick={() => markWished(c.occasion_id)}
                                >
                                    {markingOccasionId === c.occasion_id
                                        ? <RefreshCw size={14} className={styles.spinner} />
                                        : <CheckCheck size={14} />}
                                    {' '}Mark Wished
                                </button>
                            </div>
                        </div>
                    )
                })}
            </div>

            {/* Completed */}
            <div className={styles.sectionDivider} />

            <div className={styles.sectionHeaderRow}>
                <span className={styles.sectionLabel}>Completed</span>
                <span className={styles.sectionCount}>{filteredCompleted.length}</span>
            </div>

            <div className={styles.completedList}>
                {filteredCompleted.map((c) => (
                    <Link
                        key={c.occasion_id}
                        to={`/special-moments/customer/${c.client_store_relation_identifier}`}
                        className={styles.completedRow}
                    >
                        <span className={styles.completedCheck}>✅</span>
                        <div className={styles.completedText}>
                            <span className={styles.completedName}>{c.name}</span>
                            <span className={styles.completedMeta}>{c.completed_via} • {formatTime(c.completed_at)}</span>
                        </div>
                    </Link>
                ))}
                {filteredCompleted.length === 0 && (
                    <div className={styles.emptyState}>No wishes sent yet in this filter.</div>
                )}
            </div>
        </div>
    )
}

export default CSMCelebrationInbox
